import { ReactNode, useMemo } from 'react';
import { AccountBalance, Check, ChevronRight } from '@mui/icons-material';
import { LoadingButtonProps } from '@mui/lab';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Paper, PaperProps, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  InvoiceFragmentFragment,
  InvoiceStatus,
  Plan,
  ScheduledActionFragmentFragment,
  useRequestNewPlanMutation,
} from '../../api/gql';
import { getInvoiceStateProperties } from '../../utils/invoice';
import { notify } from '../../utils/notification';
import { ScheduledActions } from './ScheduledActions';
import { useSafeNavigation } from '../../hooks/useSafeNavigation';

type Props = {
  invoice: InvoiceFragmentFragment;
  transNS?: string;
};
export const InvoiceControlBox = (props: Props) => {
  const { t } = useTranslation();
  const properties = useMemo(() => getInvoiceStateProperties(props.invoice.status, props.invoice.id), [props.invoice]);
  const hasSlots = useMemo(() => props.invoice.status !== InvoiceStatus.Settled, [props.invoice.status]);

  const isPaidByConnectedBankTransactions = useMemo(() => {
    return props.invoice.status === InvoiceStatus.Settled && props.invoice.bankTransactions.length;
  }, [props.invoice]);

  return (
    <Paper
      sx={{
        borderWidth: 4,
        borderColor: properties.color,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 4,
      }}
    >
      <Stack direction={'row'} justifyContent={'space-between'} px={1} gap={2} alignItems={'center'} flexWrap={'wrap'}>
        <Stack gap={1}>
          <Stack direction={'row'} alignItems={'center'} spacing={2}>
            <Skeleton
              animation={props.invoice.status === InvoiceStatus.Reminding ? 'pulse' : false}
              sx={{
                bgcolor: properties.color,
                width: 24,
                height: 24,
                borderRadius: '100%',
                transform: 'none',
              }}
            />
            <Typography variant={'h5'}>{t(properties.transKey)}</Typography>
          </Stack>
          {isPaidByConnectedBankTransactions ? (
            <Stack pl={5} direction={'row'} gap={1}>
              <Typography variant={'subtitle2'}>{t('byBankConnection')}</Typography>
              <AccountBalance color={'info'} fontSize={'small'} />
            </Stack>
          ) : null}
        </Stack>
        <ScheduledActionsBox scheduledActions={props.invoice.scheduledActions} transNS={props.transNS} />
      </Stack>
      {hasSlots ? (
        <Stack direction={'row'} flexWrap={'wrap'} gap={4} flex={1}>
          {props.invoice.status === InvoiceStatus.Reminding ? (
            <Box flex={1}>
              <EndRemindingBox transNS={props.transNS} />
            </Box>
          ) : null}
          <Box flex={1}>
            <UpdateAmountDueBox transNS={props.transNS} />
          </Box>
          <Box flex={1}>
            <SetInvoicePaidBox transNS={props.transNS} />
          </Box>
          {props.invoice.status === InvoiceStatus.Complete && props.invoice.plan === Plan.Trial ? (
            <Box flex={1}>
              <BuyNewPlanBox invoiceId={props.invoice.id} transNS={props.transNS} />
            </Box>
          ) : null}
          {props.invoice.status === InvoiceStatus.Complete && props.invoice.plan !== Plan.Trial ? (
            <Box flex={1}>
              <EscalationBox transNS={props.transNS} />
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </Paper>
  );
};

const ScheduledActionsBox = ({
  scheduledActions,
  transNS = 'page-invoice-detail',
}: {
  scheduledActions: ScheduledActionFragmentFragment[];
  transNS?: string;
}) => {
  const { t } = useTranslation(transNS);
  return (
    <Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={2}>
      <Typography variant={'subtitle2'}>{t('checkedOut')}:</Typography>
      <ScheduledActions size={'medium'} scheduledActions={scheduledActions} />
    </Stack>
  );
};

const UpdateAmountDueBox = ({ transNS = 'page-invoice-detail', ...props }: { transNS?: string } & PaperProps) => {
  const { t } = useTranslation(transNS);
  return (
    <InfoBox
      {...props}
      title={t('updateAmountBox.title')}
      text={t('updateAmountBox.description')}
      ButtonProps={{
        children: t('updateAmountBox.action'),
        variant: 'outlined',
        component: Link,
        to: 'update-amount',
      }}
    />
  );
};
export const SetInvoicePaidBox = ({ transNS = 'page-invoice-detail', ...props }: { transNS?: string } & PaperProps) => {
  const { t } = useTranslation(transNS);
  return (
    <InfoBox
      {...props}
      title={t('setPaidBox.title')}
      text={t('setPaidBox.description')}
      ButtonProps={{
        startIcon: <Check />,
        children: t('setPaidBox.action'),
        variant: 'contained',
        component: Link,
        to: 'set-paid',
      }}
    />
  );
};

const EndRemindingBox = ({ transNS = 'page-invoice-detail', ...props }: { transNS?: string } & PaperProps) => {
  const { t } = useTranslation(transNS);
  return (
    <InfoBox
      {...props}
      title={t('endReminding.title')}
      text={t('endReminding.description')}
      ButtonProps={{
        children: t('endReminding.action'),
        variant: 'outlined',
        component: Link,
        to: 'end-reminding',
      }}
    />
  );
};
const BuyNewPlanBox = ({
  invoiceId,
  transNS = 'page-invoice-detail',
  ...rest
}: PaperProps & { invoiceId: string; transNS?: string }) => {
  const { t } = useTranslation(transNS);
  const { safeNavigate } = useSafeNavigation();
  const { mutate, isPending } = useRequestNewPlanMutation({
    onSuccess: () => {
      notify.positive({ message: t('buyNewPlan.mutation.success') });
      safeNavigate(`billing`);
    },
    onError: () => {
      notify.negative({ message: t('buyNewPlan.mutation.error') });
    },
  });

  const onClick = () => {
    mutate({
      invoiceId: invoiceId,
    });
  };

  return (
    <InfoBox
      {...rest}
      title={t('buyNewPlan.title')}
      text={t('buyNewPlan.description')}
      ButtonProps={{
        children: t('buyNewPlan.action'),
        variant: 'outlined',
        endIcon: <ChevronRight />,
        loading: isPending,
        onClick: onClick,
      }}
    />
  );
};
const EscalationBox = ({ transNS = 'page-invoice-detail', ...props }: { transNS?: string } & PaperProps) => {
  const { t } = useTranslation(transNS);
  const { safeNavigate } = useSafeNavigation();
  const onClick = () => {
    safeNavigate('escalate');
  };

  return (
    <InfoBox
      {...props}
      title={t('escalation.title')}
      text={t('escalation.description')}
      ButtonProps={{
        children: t('escalation.action'),
        variant: 'outlined',
        endIcon: <ChevronRight />,
        onClick: onClick,
      }}
    />
  );
};

const InfoBox = ({
  title,
  text,
  content,
  ButtonProps,
  sx,
  ...rest
}: {
  title: string;
  text?: string;
  content?: ReactNode;
  ButtonProps?: LoadingButtonProps<any>;
} & PaperProps) => {
  return (
    <Paper
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        flex: 1,
        gap: 2,
        p: 2,
        ...sx,
      }}
      {...rest}
    >
      {title ? <Typography variant={'h5'}>{title}</Typography> : null}
      <Box flex={1}>
        {text ? <Typography>{text}</Typography> : null}
        {content ?? null}
      </Box>
      {ButtonProps ? <LoadingButton {...ButtonProps} /> : null}
    </Paper>
  );
};
